/**
 * Team Inbox — email reply composer (spec PART 3.4).
 *
 * Sends a real email via `sendEmailToClient` (no-project) from
 * care@aayatanveda.in; the client's reply threads back into this inbox
 * (AD-014). The rich-text toolbar is shown but INERT (LATER) — no dead
 * clicks. "Internal note" tab is a visible LATER placeholder.
 *
 * Desktop: Ctrl/Cmd+Enter sends.
 */
import { useState } from 'react';

export function EmailComposer({
  toName,
  fromAddress,
  sending,
  onSend,
}: {
  /** Recipient display ("Sunita") for the placeholder. */
  toName: string;
  /** From address shown in the footer (care@aayatanveda.in). */
  fromAddress: string;
  sending: boolean;
  /** Returns true on success → clears the field. */
  onSend: (body: string) => Promise<boolean>;
}) {
  const [body, setBody] = useState('');
  const canSend = !!body.trim() && !sending;

  async function submit() {
    if (!canSend) return;
    const ok = await onSend(body.trim());
    if (ok) setBody('');
  }

  return (
    <div className="ti-ecomposer">
      <div className="ti-ec-tabs" role="tablist" aria-label="Reply mode">
        <button type="button" className="ti-ec-tab on" role="tab" aria-selected>
          Reply by Email
        </button>
        <button
          type="button"
          className="ti-ec-tab"
          role="tab"
          aria-selected={false}
          disabled
          title="Internal team-only note — ships in Phase 4"
        >
          Internal note <span className="ti-later">LATER</span>
        </button>
      </div>

      {/* Rich-text toolbar — shown but inert (LATER). */}
      <div className="ti-ec-tools" aria-hidden>
        <button type="button" className="ti-ec-tool" disabled title="Formatting ships later">
          <b>B</b>
        </button>
        <button type="button" className="ti-ec-tool" disabled title="Formatting ships later">
          <i>i</i>
        </button>
        <button type="button" className="ti-ec-tool" disabled title="Formatting ships later">
          🔗
        </button>
        <button type="button" className="ti-ec-tool" disabled title="Attachments ship later">
          📎
        </button>
        <span className="ti-ec-toolnote">formatting — LATER</span>
      </div>

      <textarea
        className="ti-ec-field"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void submit();
          }
        }}
        placeholder={`Type your reply… (sends as a real email to ${toName})`}
        aria-label="Email reply"
        rows={3}
        disabled={sending}
      />

      <div className="ti-ec-row">
        <span className="ti-ec-from">
          From: <b>{fromAddress}</b> · their reply threads back into this inbox
        </span>
        <button
          type="button"
          className="ti-ec-send"
          disabled={!canSend}
          onClick={() => void submit()}
        >
          {sending ? 'Sending…' : 'Send email ➤'}
        </button>
      </div>
    </div>
  );
}
